<script setup>
const emit = defineEmits(['tokenized', 'payment-error'])
const props = defineProps({
  amount: { type: Number, required: true },
  buttonStyle: {
    type: Object,
    required: false,
    default: () => ({
      label: 'paypal',
      size: 'large',
      color: 'white',
      shape: 'rect',
      tagline: 'false',
    }),
  },
  increasePayPalSize: {
    type: Boolean,
    required: false,
    default: false,
  },
  checkoutWithPayPal: { type: Boolean, required: false, default: false },
  savePaymentDetails: { type: Boolean, required: false, default: true },
  showEmail: { type: Boolean, default: true },
})
const { $auth, $config } = useNuxtApp()
const error = ref('')
const email = ref(null)
const braintreeStore = useBraintree()
const loggedIn = computed(() => $auth.loggedIn)
const created = ref(false)

// Retry logic for DOM readiness
async function waitForPayPalButton(maxAttempts = 10, delay = 50) {
  let attempts = 0
  while (attempts < maxAttempts) {
    if (document.getElementById('paypal-button')) return true
    await new Promise((resolve) => setTimeout(resolve, delay))
    attempts++
  }
  return false
}

const computedContainerStyle = computed(() => {
  if (props.increasePayPalSize) {
    return 'min-width:350px;width:100%;max-width:500px;'
  }
  return props.buttonContainerStyle || ''
})

async function createClient() {
  const braintree = await import('braintree-web')
  const client = await braintreeStore.getClientInstance()
  if (created.value) return

  // Wait for #paypal-button to exist in the DOM
  const found = await waitForPayPalButton()
  if (!found) {
    error.value = 'PayPal button container not found.'
    emit('payment-error', new Error('PayPal button container not found.'))
    return
  }

  braintree.paypalCheckout.create(
    { client },
    function (paypalCheckoutErr, paypalCheckoutInstance) {
      if (paypalCheckoutErr) {
        error.value = 'Failed to initialize PayPal Checkout.'
        emit('payment-error', paypalCheckoutErr)
        return
      }
      const paymentParams = {
        flow: 'checkout',
        singleUse: true,
        intent: 'authorize',
        amount: props.amount,
        displayName: $config.public.applePayMerchantName,
        currency: 'USD',
      }
      let useVault = false
      if (
        props.savePaymentDetails &&
        ($auth.loggedIn || props.checkoutWithPayPal)
      ) {
        paymentParams.flow = 'vault'
        paymentParams.singleUse = false
        paymentParams.intent = 'sale'
        useVault = true
      }
      paypalCheckoutInstance.loadPayPalSDK({ vault: useVault }, function () {
        if (!window.paypal || !window.paypal.Buttons) {
          error.value = 'PayPal SDK failed to load.'
          emit('payment-error', new Error('PayPal SDK failed to load.'))
          return
        }
        paypal
          .Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            style: props.buttonStyle,
            ...(useVault
              ? {
                  createBillingAgreement: function () {
                    return paypalCheckoutInstance.createPayment(paymentParams)
                  },
                }
              : {
                  createOrder: function () {
                    return paypalCheckoutInstance.createPayment(paymentParams)
                  },
                }),
            onApprove: function (data, actions) {
              paypalCheckoutInstance.tokenizePayment(
                data,
                function (tokenizeErr, payload) {
                  if (tokenizeErr) {
                    if (tokenizeErr.type !== 'CUSTOMER') {
                      error.value =
                        'An error occurred while processing the paypal payment.'
                      emit('payment-error', tokenizeErr)
                    }
                    return
                  }
                  emit('tokenized', payload)
                },
              )
            },
            onCancel: function () {},
            onError: function (err) {
              emit('payment-error', err)
            },
          })
          .render('#paypal-button')
      })
    },
  )
  created.value = true
}

function cleanupPayPalButton() {
  const btn = document.getElementById('paypal-button')
  if (btn) btn.innerHTML = ''
  created.value = false
  error.value = ''
}

function initializePayPalButton() {
  if (created.value) return
  error.value = ''
  created.value = false
  nextTick().then(() => {
    createClient()
  })
}

watch(
  () => loggedIn.value,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      createClient()
    }
  },
)

onMounted(() => {
  initializePayPalButton()
})

onActivated(() => {
  initializePayPalButton()
})

onUnmounted(() => {
  cleanupPayPalButton()
})

onDeactivated(() => {
  cleanupPayPalButton()
})
</script>

<template>
  <ClientOnly>
    <div class="container" style="padding-top: 20px">
      <div class="col-6 offset-3">
        <div class="card bg-light">
          <div class="card-body">
            <form>
              <div id="paypal-button" :style="computedContainerStyle" />
              <span v-if="error" class="error">Error: {{ error }}</span>
            </form>
          </div>
        </div>
      </div>
      <p v-if="email && showEmail" class="paypal-email">
        {{ email }}
      </p>
    </div>
  </ClientOnly>
</template>
